import { OwnerResponse, RecipientResponse } from '@/modules/Drive/types'
import { PermissionLevel } from '@/modules/Drive/utils/DrivePermission/permission'
import AccountCard from './AccountCard'
import { t } from 'i18next'
import { randomAvatarSrc } from '@/utils/function'
import { Avatar } from 'vnetwork-ui'

interface ShareRecipientsProps {
    recipients: RecipientResponse[]
    onDirtyChange: (
        isDirty: boolean,
        recipient_id: number,
        permission_level: PermissionLevel
    ) => void
    activeTab: 'employee' | 'department' | 'public'
    isDirty: boolean
    owner?: OwnerResponse
}

const ShareRecipients = ({
    recipients,
    onDirtyChange,
    activeTab,
    isDirty,
    owner,
}: ShareRecipientsProps) => {
    const employees = recipients.filter(
        (r) => r.share_recipient_type === 'employee'
    )
    const departments = recipients.filter(
        (r) => r.share_recipient_type === 'department'
    )

    return (
        <div className="flex flex-col">
            <div
                className={`overflow-y-auto ${isDirty ? 'h-[50vh]' : 'h-[20vh]'}`}
            >
                <div className={activeTab === 'employee' ? 'block' : 'hidden'}>
                    {owner && <OwnerCard key={owner.owner_id} owner={owner} />}
                    {employees.length > 0 ? (
                        <div className="flex flex-col">
                            {employees.map((recipient) => (
                                <AccountCard
                                    key={recipient.recipient_id}
                                    recipient={recipient}
                                    onDirtyChange={(
                                        isDirty,
                                        permission_level
                                    ) =>
                                        onDirtyChange(
                                            isDirty,
                                            recipient.recipient_id,
                                            permission_level as PermissionLevel
                                        )
                                    }
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="w-full mt-2 py-3 flex flex-col gap-1 bg-gray-50 items-center">
                            <p className="text-sm text-center text-gray-400">
                                {t('no-employee-shared')}
                            </p>
                        </div>
                    )}
                </div>
                <div
                    className={activeTab === 'department' ? 'block' : 'hidden'}
                >
                    {departments.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            {departments.map((recipient) => (
                                <AccountCard
                                    key={recipient.recipient_id}
                                    recipient={recipient}
                                    onDirtyChange={(
                                        isDirty,
                                        permission_level
                                    ) =>
                                        onDirtyChange(
                                            isDirty,
                                            recipient.recipient_id,
                                            permission_level as PermissionLevel
                                        )
                                    }
                                />
                            ))}
                        </div>
                    ) : (
                        <div
                            className={`w-full mt-2 py-3 flex flex-col gap-1 bg-gray-50 items-center`}
                        >
                            <p className="text-sm text-center text-gray-400">
                                {t('no-department-shared')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const OwnerCard = ({ owner }: { owner: OwnerResponse }) => {
    return (
        <div className="px-4 py-2 hover:bg-gray-50">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Avatar
                        src={randomAvatarSrc(owner.owner_email)}
                        size="medium"
                    />

                    <div className="flex flex-col">
                        <span className="text-gray-900 font-medium text-sm">
                            {owner.owner_name}
                        </span>
                        <span className="text-gray-500 text-xs">
                            {owner.owner_email}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-1">
                        {t('owner')}
                    </span>
                </div>
            </div>
        </div>
    )
}

export default ShareRecipients
